import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowUpRight, Linkedin } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_solar-scada-monitor/artifacts/k1rohnzb_Logo.png";

const TRANSLATIONS = {
  en: {
    description: "Engineering company delivering automation and software for energy systems. PPC, SCADA, BESS, JANUS and BMS. Since 2008.",
    btnProducts: "View products",
    btnContact: "Get in touch",
    navTitle: "Navigation",
    navHome: "Home",
    navProducts: "Products",
    navAbout: "About",
    navProjects: "Projects",
    navPartners: "Partners",
    navContact: "Contacts",
    contactTitle: "Contacts"
  },
  pt: {
    description: "Empresa de engenharia focada no desenvolvimento de automação e software para sistemas de energia. PPC, SCADA, BESS, JANUS e BMS. Desde 2008.",
    btnProducts: "Ver produtos",
    btnContact: "Entrar em contacto",
    navTitle: "Navegação",
    navHome: "Início",
    navProducts: "Produtos",
    navAbout: "Sobre Nós",
    navProjects: "Projetos",
    navPartners: "Parceiros",
    navContact: "Contactos",
    contactTitle: "Contactos"
  }
};

export default function Footer() {
  const { language } = useLanguage();
  const location = useLocation();
  const t = TRANSLATIONS[language];

  const handleNavClick = (to) => {
    if (location.pathname === to) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative z-10 border-t border-white/10 bg-black/70 backdrop-blur-xl"
    >
      {/* Reduzi py-10 para py-6 */}
      <div className="mx-auto max-w-[1400px] px-6 py-6 md:px-12 lg:px-16">
        {/* Reduzi gap-10 para gap-8 */}
        <div className="grid gap-8 lg:grid-cols-12">
          
          {/* COLUNA 1 */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="WSBP" className="h-8 w-auto" loading="lazy" decoding="async" />
            </div>
            <p className="mt-3 max-w-md text-xs text-white/70">
              {t.description}
            </p>
            {/* Reduzi mt-6 para mt-4 */}
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to="/products" onClick={() => handleNavClick("/products")} className="btn-secondary py-1.5 px-3 text-[10px]" data-testid="footer-products">
                {t.btnProducts} <ArrowUpRight size={12} />
              </Link>
              <Link to="/contact" onClick={() => handleNavClick("/contact")} className="btn-primary py-1.5 px-3 text-[10px]" data-testid="footer-contact">
                {t.btnContact} <ArrowUpRight size={12} />
              </Link>
            </div>
          </div>

          {/* COLUNA 2 */}
          <div className="lg:col-span-3">
            <div className="eyebrow text-xs">{t.navTitle}</div>
            {/* Reduzi mt-4 para mt-2 e o espaço entre itens */}
            <ul className="mt-2 space-y-1 font-display text-sm text-white/85">
              <li><Link to="/" onClick={() => handleNavClick("/")} className="hover:text-[#F68A31] transition-colors">{t.navHome}</Link></li>
              <li><Link to="/products" onClick={() => handleNavClick("/products")} className="hover:text-[#F68A31] transition-colors">{t.navProducts}</Link></li>
              <li><Link to="/about" onClick={() => handleNavClick("/about")} className="hover:text-[#F68A31] transition-colors">{t.navAbout}</Link></li>
              <li><Link to="/projects" onClick={() => handleNavClick("/projects")} className="hover:text-[#F68A31] transition-colors">{t.navProjects}</Link></li>
              <li><Link to="/partners" onClick={() => handleNavClick("/partners")} className="hover:text-[#F68A31] transition-colors">{t.navPartners}</Link></li>
              <li><Link to="/contact" onClick={() => handleNavClick("/contact")} className="hover:text-[#F68A31] transition-colors">{t.navContact}</Link></li>
            </ul>
          </div>

          {/* COLUNA 3 */}
          <div className="lg:col-span-4">
            <div className="eyebrow text-xs">{t.contactTitle}</div>
            {/* Reduzi mt-4 para mt-2 e o texto para text-xs */}
            <ul className="mt-2 space-y-2 text-xs text-white/85">
              <li className="flex items-start gap-2">
                <Mail size={14} className="mt-0.5 text-[#F68A31]" />
                <a href="mailto:info@wsbp.eu" className="hover:text-[#F68A31] transition-colors">info@wsbp.eu</a>
              </li>
              <li className="flex items-start gap-2">
                <Phone size={14} className="mt-0.5 text-[#F68A31]" />
                <a href="tel:+351239163715" className="hover:text-[#F68A31] transition-colors">+351 239 163 715</a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin size={14} className="mt-0.5 text-[#F68A31]" />
                <a href="https://maps.app.goo.gl/ezyV6HiT7FZBd6od7" target="_blank" rel="noreferrer" className="leading-snug hover:text-[#F68A31] transition-colors">
                  Estádio Cidade de Coimbra, Rua D. Manuel I, 78, Piso 2, Sala 1, 3030-327 Coimbra
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Linkedin size={14} className="mt-0.5 text-[#F68A31]" />
                <a href="https://www.linkedin.com/company/wsbp-electronics/" target="_blank" rel="noreferrer" className="hover:text-[#F68A31] transition-colors">
                  linkedin.com/company/wsbp-electronics
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* RODAPÉ FINAL */}
        {/* Reduzi mt-10 para mt-6 e pt-6 para pt-4 */}
        <div className="mt-6 flex flex-col items-start justify-between gap-2 border-t border-white/10 pt-4 text-[9px] uppercase tracking-[0.2em] text-white/40 md:flex-row md:items-center">
          <span>© {new Date().getFullYear()} WSBP — We Solve Building Problems</span>
          <span className="hidden md:block">PPC · SCADA · BESS · JANUS · BMS</span>
        </div>
      </div>
    </footer>
  );
}